import { GRASS_CHARACTERISTICS } from '../data/grass-characteristics'
import { getRegionFromZipCode } from '../data/regional-climate'

const GRASS_TYPES = Object.keys(GRASS_CHARACTERISTICS)

// Normalize tolerance strings to numeric values (1-5).
// Handles hyphenated values like "Medium-High" → 4 and "Medium-Low" → 2 by
// matching the highest-specificity tier first. Pre-fix, plain `includes` order
// could mis-rank ("Medium-Low" matching `low` before the medium check).
export function toleranceToNumber(value: string | undefined): number {
  if (!value) return 3
  const lower = value.toLowerCase()
  // Compound modifiers first (most specific)
  if (lower.includes('very low')) return 1
  if (lower.includes('very high')) return 5
  if (lower.includes('medium-high') || lower.includes('moderate-high')) return 4
  if (lower.includes('medium-low') || lower.includes('moderate-low')) return 2
  // Then plain modifiers
  if (lower.includes('low')) return 2
  if (lower.includes('high')) return 4
  if (lower.includes('medium') || lower.includes('moderate')) return 3
  return 3
}

// Map URL-friendly slugs (lowercase, dash-separated) back to canonical
// GRASS_TYPES keys (e.g. "kentucky-bluegrass" -> "Kentucky Bluegrass").
export function slugToGrassName(slug: string): string | null {
  const normalized = slug.toLowerCase().replace(/[-_]+/g, ' ').trim()
  return GRASS_TYPES.find(name => name.toLowerCase() === normalized) ?? null
}

// Parse the ?grasses= URL param into canonical grass names. Drops invalid
// slugs, dedupes (preserving first-occurrence order), and caps at 3. Returns
// the matched list as-is; caller decides what to do when length < 2.
export function parseGrassesParam(raw: string | null): string[] {
  if (!raw) return []
  const seen = new Set<string>()
  const matched: string[] = []
  for (const part of raw.split(',')) {
    const name = slugToGrassName(part.trim())
    if (!name) continue
    if (seen.has(name)) continue
    seen.add(name)
    matched.push(name)
    if (matched.length >= 3) break
  }
  return matched
}

// Parse the ?zip= URL param. Returns the 5-digit string when it resolves to a
// known region, otherwise null. We don't surface a "couldn't find your zone"
// error on the tool — silent fall-through to the no-zone scoring is enough.
export function parseZipParam(raw: string | null): string | null {
  if (!raw) return null
  const trimmed = raw.trim()
  if (!/^\d{5}$/.test(trimmed)) return null
  return getRegionFromZipCode(trimmed) === 'Unknown' ? null : trimmed
}
